import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AdminStatsResponse } from '@masha/shared';
import { api } from '../../lib/api';

const kzt = new Intl.NumberFormat('ru-RU');

function formatKzt(amount: number): string {
  return `${kzt.format(amount)} ₸`;
}

function formatUsd(amount: number | null): string {
  if (amount === null) return '—';
  // Sub-cent LLM costs (e.g. $0.002) round to $0.00 with 2 decimals — show more precision below $1.
  const decimals = amount > 0 && amount < 1 ? 4 : 2;
  return `$${amount.toFixed(decimals)}`;
}

function formatPercent(share: number): string {
  return `${(share * 100).toFixed(1)}%`;
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="p-3 rounded-xl bg-tg-section-bg">
      <div className="text-xs uppercase opacity-60 mb-2">{title}</div>
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="opacity-70">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

function StatsBlock({ stats }: { stats: AdminStatsResponse }) {
  const { users, money, usage, llm } = stats;

  return (
    <div className="space-y-2 mb-6">
      <Card title="Пользователи">
        <Row label="Всего" value={String(users.total)} />
        <Row label="Прошли онбординг" value={String(users.onboarded)} />
        <Row label="Новых за 7 дней" value={String(users.new_7d)} />
        <Row label="Новых за 30 дней" value={String(users.new_30d)} />
        <Row label="По рефералке" value={String(users.referred)} />
      </Card>

      <Card title="Деньги">
        <Row
          label="Платящих"
          value={`${money.paying_users} (${formatPercent(money.paying_share)})`}
        />
        <Row label="Оплат" value={String(money.payments_count)} />
        <Row label="Выручка" value={formatKzt(money.revenue_kzt)} />
        <Row label="Купили повторно" value={String(money.repeat_buyers)} />
      </Card>

      <Card title="Генерации">
        <Row label="Всего" value={String(usage.generations_total)} />
        <Row label="За 7 дней" value={String(usage.generations_7d)} />
        <Row label="Генерировали хоть раз" value={String(usage.users_with_generation)} />
        <Row label="Конверсия в генерацию" value={formatPercent(usage.generation_conversion)} />
      </Card>

      <Card title="LLM">
        <Row label="Потрачено всего" value={formatUsd(llm.total_spent_usd)} />
        <Row label="Остаток на балансе" value={formatUsd(llm.balance_usd)} />
        <Row label="За 7 дней" value={formatUsd(llm.spent_7d_usd)} />
        <Row label="За 30 дней" value={formatUsd(llm.spent_30d_usd)} />
        <Row
          label="В среднем за сценарий (30 дней)"
          value={formatUsd(llm.avg_per_generation_30d_usd)}
        />
        {llm.total_spent_usd === null && (
          <div className="text-xs opacity-60 pt-1">OpenRouter недоступен — прочерки выше</div>
        )}
      </Card>
    </div>
  );
}

export function AdminDashboard() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => api.adminGetStats(),
    staleTime: 60_000,
  });

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto">
      <h1 className="text-2xl font-bold mb-6">Админка</h1>

      {isLoading && <div className="mb-6 text-sm opacity-60">Загрузка статистики…</div>}

      {error && (
        <div className="mb-6 p-3 rounded-xl bg-tg-section-bg text-sm">
          <div className="mb-2">Не удалось загрузить статистику.</div>
          <button type="button" className="underline" onClick={() => refetch()}>
            Повторить
          </button>
        </div>
      )}

      {data && <StatsBlock stats={data} />}

      <div className="space-y-2">
        <Link to="/admin/ideas" className="block p-3 rounded-xl bg-tg-section-bg">
          Идеи Reels
        </Link>
        <Link to="/admin/goals" className="block p-3 rounded-xl bg-tg-section-bg">
          Цели маркетинга
        </Link>
        <Link to="/" className="block p-3 rounded-xl text-sm opacity-70">
          ← На главную
        </Link>
      </div>
    </div>
  );
}
